import { Controller } from '@hotwired/stimulus';
import TomSelect from 'tom-select';

export default class extends Controller {
  static targets = [
    'select',
    'contextIdField',
    'contextTypeField',
    'startDateField',
    'endDateField',
    'startTimeField',
    'endTimeField',
    'startTimeMeridianField',
    'endTimeMeridianField'
  ];
  activities = {};

  connect() {
    const controller = this;

    this.tomSelect = new TomSelect(this.selectTarget, {
      hideSelected: true,
      persist: false,
      valueField: 'title',
      labelField: 'title',
      searchField: 'title',
      allowEmptyOption: true,
      create: true,
      load: this.#searchActivities.bind(this),
      onItemAdd: this.#handleItemAdded.bind(this),
      onItemRemove: this.#handleItemRemoved.bind(this),
      render: {
        option: this.#setUpSelectableElement,
        item: this.#setUpSelectableElement
      },
      onInitialize: function (e) {
        const innitialValue = this.getValue();
        if (innitialValue && controller.hasContextIdFieldTarget) {
          const isActivityEvent = !!controller.contextIdFieldTarget.value;

          this.updateOption(innitialValue, {
            title: innitialValue,
            value: innitialValue,
            id: controller.contextIdFieldTarget.value
          });

          if (isActivityEvent) {
            controller.#lockDateRelatedFields();
          }
        }
      }
    })
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy();
    }
  }

  async #searchActivities(query, callback) {
    const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute('content');
    try {
      const response = await fetch(`/activities/select_options?query=${encodeURIComponent(query)}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        }
      });
      const data = await response.json();
      this.activities = { ...this.activities, ...this.#activitiesParser(data) };
      callback(data);
    } catch (error) {
      callback([]);
    }
  }

  #activitiesParser(data) {
    return data.reduce((acc, curr) => {
      acc[curr.id.toString()] = curr;

      return acc;
    }, {});
  }

  #setUpSelectableElement(data, cscape) {
    return `<div class="option" data-activity-id="${data.id ? cscape(data.id) : ''}">${cscape(data.title)}</div>`;
  }

  #handleItemAdded(_, data) {
    if (!this.hasContextIdFieldTarget || !this.hasContextTypeFieldTarget) return;

    const contextIdField = this.contextIdFieldTarget;
    const contextTypeField = this.contextTypeFieldTarget;

    const activityId = data?.dataset?.activityId;

    const isActivityEvent = !!activityId;

    contextIdField.value = activityId;
    contextTypeField.value = isActivityEvent ? 'SingleEvent' : '';

    if (isActivityEvent) {
      const activitySelected = this.activities[activityId];
      this.#fillAndLockDatesFromActivitySelected(activitySelected);
    }
  }

  #handleItemRemoved(_, el) {
    const activityId = el?.dataset?.activityId;
    const isActivityEvent = !!activityId;

    if (isActivityEvent) {
      this.#freeDatesForEdition();
    }

    if (!this.hasContextIdFieldTarget || !this.hasContextTypeFieldTarget) return;

    const contextIdField = this.contextIdFieldTarget;
    const contextTypeField = this.contextTypeFieldTarget;
    contextIdField.value = '';
    contextTypeField.value = '';
  }

  #lockDateRelatedFields() {
    this.startDateFieldTarget.classList.add('field-disabled');
    this.endDateFieldTarget.classList.add('field-disabled');

    this.startTimeFieldTarget.classList.add('field-disabled');
    this.endTimeFieldTarget.classList.add('field-disabled');

    this.startTimeMeridianFieldTarget.classList.add('field-disabled');
    this.endTimeMeridianFieldTarget.classList.add('field-disabled');
  }

  #fillAndLockDatesFromActivitySelected(activity) {
    // Set Activity Dates
    if (!activity || !this.hasStartDateFieldTarget || !this.hasEndDateFieldTarget) return;

    const startDateFieldController = this.application.getControllerForElementAndIdentifier(this.startDateFieldTarget, 'easepick');
    const endDateFieldController = this.application.getControllerForElementAndIdentifier(this.endDateFieldTarget, 'easepick');

    const eventStarts = new Date(activity.started_at);
    const eventEnds = new Date(activity.finished_at);

    startDateFieldController.populateAndLockField(eventStarts);
    endDateFieldController.populateAndLockField(eventEnds);

    // Set Activity Times
    if (!this.hasStartTimeFieldTarget || !this.hasEndTimeFieldTarget) return;

    const startTimeFieldController = this.application.getControllerForElementAndIdentifier(this.startTimeFieldTarget, 'imask');
    const endTimeFieldController = this.application.getControllerForElementAndIdentifier(this.endTimeFieldTarget, 'imask');

    const options = { hour: '2-digit', minute: '2-digit', hour12: true };
    const [startTime, starTimeMeridian] = eventStarts.toLocaleTimeString([], options)?.split(' ') || [];
    const [endTime, endTimeMeridian] = eventEnds.toLocaleTimeString([], options)?.split(' ') || [];

    startTimeFieldController.populateAndLockField(startTime);
    endTimeFieldController.populateAndLockField(endTime);

    // Set Activity Times Meridian
    if (!this.hasStartTimeMeridianFieldTarget || !this.hasEndTimeMeridianFieldTarget) return;

    this.#populateAndLockDateMeridianField(this.startTimeMeridianFieldTarget, starTimeMeridian?.toUpperCase());
    this.#populateAndLockDateMeridianField(this.endTimeMeridianFieldTarget, endTimeMeridian?.toUpperCase());
  }

  #freeDatesForEdition() {
    // Clean Activity Dates
    if (!this.hasStartDateFieldTarget || !this.hasEndDateFieldTarget) return;

    const startDateFieldController = this.application.getControllerForElementAndIdentifier(this.startDateFieldTarget, 'easepick');
    const endDateFieldController = this.application.getControllerForElementAndIdentifier(this.endDateFieldTarget, 'easepick')
    startDateFieldController?.emptyAndFreeField();
    endDateFieldController?.emptyAndFreeField();

    // Clean Activity Times
    if (!this.hasStartTimeFieldTarget || !this.hasEndTimeFieldTarget) return;

    const startTimeFieldController = this.application.getControllerForElementAndIdentifier(this.startTimeFieldTarget, 'imask');
    const endTimeFieldController = this.application.getControllerForElementAndIdentifier(this.endTimeFieldTarget, 'imask')
    startTimeFieldController?.emptyAndFreeField();
    endTimeFieldController?.emptyAndFreeField();

    // Clean Activity Times Meridian
    if (!this.hasStartTimeMeridianFieldTarget || !this.hasEndTimeMeridianFieldTarget) return;

    this.#emptyAndFreeDateMeridianField(this.startTimeMeridianFieldTarget);
    this.#emptyAndFreeDateMeridianField(this.endTimeMeridianFieldTarget);
  }

  #populateAndLockDateMeridianField(el, val) {
    el.value = val;
    el.classList.add('field-disabled');
  }

  #emptyAndFreeDateMeridianField(el) {
    el.value = 'AM';
    el.classList.remove('field-disabled');
  }

  exploreMoreActivities() {
    if (!this.hasStartDateFieldTarget || !this.hasEndDateFieldTarget) return;
    Turbo.visit(`/activities?started_at=${encodeURIComponent(this.startDateFieldTarget.value)}&finished_at=${encodeURIComponent(this.endDateFieldTarget.value)}`);
  }
}
